import { AfterViewInit, Component, inject } from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem, MessageService } from 'primeng/api';
import { SidebarService } from '../../../services/menu/sidebar.service';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { TableModule } from 'primeng/table';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { TableListActionsComponent } from '../../molecules/table-list-actions/table-list-actions.component';
import { SidebarModule } from 'primeng/sidebar';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { Commune } from '../../../models/commune';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { NgIf } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { UserListComponent } from '../../molecules/user-list/user-list.component';

@Component({
  selector: 'app-commune-list',
  standalone: true,
  imports: [
    TableModule,
    TableCaptionComponent,
    TableListActionsComponent,
    SidebarModule,
    HeaderComponent,
    MaskComponent,
    RouterOutlet,
    NgIf,
    ButtonDirective,
    RouterLink,
    UserListComponent,
  ],
  templateUrl: './commune-list.component.html',
  styleUrl: './commune-list.component.scss',
})
export class CommuneListComponent
  extends BaseListComponent<Commune>
  implements AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-8',
    icon: 'fas fa-city',
    headerText: 'Kommune',
  };

  readonly store = inject(CommuneStore);
  override menuItems: MenuItem[] = [];
  override sidebarId = 'commune-administration';
  activeCommune?: string;

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
  }

  /**
   * Sets the active commune by assigning the provided commune ID.
   *
   * @param {string} communeId - The unique identifier of the commune to be set as active.
   * @return {void} This method does not return a value.
   */
  setActiveCommune(communeId: string): void {
    this.activeCommune = communeId;
  }

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * This method sets the sidebar configuration using the SidebarService.
   *
   * @return {void} Does not return a value.
   */
  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  /**
   * Handles the closure of commune users, resetting the active commune.
   * This method is triggered to clear the current active commune configuration.
   *
   * @return {void} Does not return a value.
   */
  onCommuneUsersClose(): void {
    this.activeCommune = undefined;
  }

  /**
   * Copies the commune link to the clipboard.
   *
   * @param {string} id - The unique identifier of the commune for which the link will be generated and copied.
   * @return {void} This method does not return a value.
   */
  copyCommuneLink(id: string): void {
    const link = this.routingService.getOpenAtlasLink(id);
    navigator.clipboard.writeText(link).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Link kopiert',
        detail: 'Der Link wurde in die Zwischenablage kopiert.',
      });
    });
  }
}
